import { LogService } from '@awork/_shared/services/log-service/log.service'
import {
  Component,
  DestroyRef,
  NgZone,
  Renderer2,
  ViewContainerRef,
  computed,
  effect,
  inject,
  signal,
  viewChild
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { StatusBar, Style } from '@capacitor/status-bar'
import { DeeplinkService } from './shared/services/deeplink-service/deeplink.service'
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router'
import { ModalService } from './shared/services/modal-service/modal.service'
import type { Observable } from 'rxjs'
import { EMPTY, catchError, exhaustMap, filter, finalize, from, pairwise, switchMap, take, takeUntil, tap } from 'rxjs'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { SplashScreen } from '@capacitor/splash-screen'
import { environment } from '@awork/environments/environment'
import { AppService } from './app.service'
import type { MobileThemeMode } from './app.service'
import { App } from '@capacitor/app'
import { TextZoom } from '@capacitor/text-zoom'
import { Device } from '@capacitor/device'
import { Location } from '@angular/common'
import { PopupService } from './shared/services/popup-service/popup.service'

import { init as sentryCapacitorInit, setTag } from '@sentry/capacitor'
import { breadcrumbsIntegration, init } from '@sentry/angular'
import sentryBuild from '../sentry-build.json'
import { OverlayStatusBarService } from './shared/services/overlay-status-bar/overlay-status-bar.service'
import { readScreenBackground, toHexColor } from './shared/functions/screen-background'
import { Capacitor } from '@capacitor/core'
import { MobileVersionService } from './shared/services/mobile-version-service/mobile-version.service'
import { MobileUpdateService } from './shared/services/mobile-update-service/mobile-update.service'
import { CriticalUpdateService } from './shared/services/critical-update-service/critical-update.service'
import { CriticalUpdateScreenComponent } from './framework/components/critical-update-screen/critical-update-screen.component'
import { ButtonComponent } from './shared/components/button/button.component'
import { ToastComponent } from './shared/components/toast/toast.component'
import { KeyboardDismissService } from './shared/services/keyboard-dismiss-service/keyboard-dismiss.service'
import { ScrollToTopService } from './shared/services/scroll-to-top-service/scroll-to-top.service'
import { ToastService } from './shared/services/toast-service/toast.service'
import type { ToastEntry } from './shared/services/toast-service/toast.service'
import { CustomFieldSaveService } from '@awork/features/project/services/custom-field/custom-field-save.service'

const STATUS_BAR_BACKGROUND = {
  light: '#ffffff',
  dark: '#1c1d28'
} as const

// Matches the web scrim (Ease shadow color at 40% opacity over the screen surface).
const OVERLAY_STATUS_BAR_BACKGROUND = {
  light: '#a1abba',
  dark: '#111118'
} as const

// The iOS shell registers this handler (see ThemeViewController). Older app builds do not have it.
type NativeAppearanceWindow = Window & {
  webkit?: {
    messageHandlers?: {
      awAppAppearance?: { postMessage: (message: { mode: MobileThemeMode; background: string | null }) => void }
    }
  }
}

@Component({
  selector: 'aw-app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.scss',
  imports: [RouterOutlet, CriticalUpdateScreenComponent, ButtonComponent, ToastComponent]
})
export class AppComponent {
  private readonly toastService = inject(ToastService)
  protected readonly toastEntries = this.toastService.entries
  public viewRef = inject(ViewContainerRef)
  public overlayViewRef = viewChild('overlayRoot', { read: ViewContainerRef })
  public deepLink = inject(DeeplinkService)
  private router = inject(Router)
  private modalService = inject(ModalService)
  private popupService = inject(PopupService)
  private logService = inject(LogService)
  private mobileVersionService = inject(MobileVersionService)
  private mobileUpdateService = inject(MobileUpdateService)
  private criticalUpdateService = inject(CriticalUpdateService)
  private renderer = inject(Renderer2)
  private appService = inject(AppService)
  private accountService = inject(AccountService)
  private overlayStatusBar = inject(OverlayStatusBarService)
  private location = inject(Location)
  private ngZone = inject(NgZone)
  private destroyRef = inject(DestroyRef)
  private keyboardDismiss = inject(KeyboardDismissService)
  private scrollToTop = inject(ScrollToTopService)
  private readonly customFieldSaveService = inject(CustomFieldSaveService)
  private nativeReady = false
  private navigationReady = false
  private launchUpdateChecked = false
  private splashHidden = false

  isDarkMode = false
  protected readonly showCriticalUpdate = computed(() => this.criticalUpdateService.state().status !== 'none')
  protected readonly failedNavigationUrl = signal<string | null>(null)
  protected readonly retryingNavigation = signal(false)
  protected readonly commonTranslations = q.translations.common

  constructor() {
    this.renderer.addClass(document.body, 'aw-theme')
    this.initSentry()
    this.initializeApp().then(() => {})
    this.destroyRef.onDestroy(this.keyboardDismiss.listen())
    this.destroyRef.onDestroy(this.scrollToTop.listen())

    toObservable(this.appService.onlineStatus)
      .pipe(
        pairwise(),
        filter(([wasOnline, isOnline]) => !wasOnline && isOnline),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => void this.retryNavigation())

    // Field saves continue after details close, so the app reports their failures.
    this.customFieldSaveService.failures
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toastService.show(q.translations.errors.somethingWentWrong, 'warning'))

    // Keep the theme in sync with the system appearance while the app runs.
    effect(() => {
      const isDark = this.appService.darkMode()

      // Re-run on every native push: the StatusBar plugin resets its background to the configured
      // color each time the Capacitor view appears again, for example after a native sheet closes.
      this.appService.nativeThemeSync()
      this.overlayStatusBar.hasActiveOverlay()
      this.setDarkMode(isDark)
      this.scheduleStatusBarSync()
      this.syncNativeAppearance(this.appService.themeMode())
    })
  }

  /**
   * Removes the toast after its exit completes.
   * @param {ToastEntry} entry Dismissed toast.
   */
  protected dismissToast(entry: ToastEntry): void {
    this.toastService.dismiss(entry)
  }

  private async initializeApp() {
    // Capture incoming links before unrelated native plugin setup can delay or fail.
    void this.deepLink.initDeepLinking()
    // Keep the splash screen while the updater may still replace this bundle with a newer one.
    void this.mobileUpdateService.waitForLaunchCheck().then(() => {
      this.launchUpdateChecked = true
      void this.hideSplashWhenReady()
    })
    this.router.events
      .pipe(
        tap(event => {
          if (event instanceof NavigationEnd) {
            this.navigationReady = true
            this.failedNavigationUrl.set(null)
            this.removeLoader()
            void this.hideSplashWhenReady()
            // The new screen paints after this event; pick up its background then.
            this.scheduleStatusBarSync()
          }
        }),
        filter((event): event is NavigationError => event instanceof NavigationError && !this.navigationReady),
        exhaustMap(event => this.recoverNavigation(event.url)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()

    from(Device.getInfo())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async deviceInfo => {
        const isIOS = deviceInfo.platform === 'ios'

        await this.syncStatusBarTheme(this.appService.darkMode())

        this.nativeReady = true
        void this.hideSplashWhenReady()

        // add a main class to make different styles possible (e.f. used for the blurry background on ios)
        document.body.classList.add(isIOS ? 'ios' : 'android')
      })

    // disable native text zoom settings
    await TextZoom.set({ value: 1 })

    this.handleBackNavigation()

    // Theme Detection
    this.setDarkMode(this.appService.darkMode())
  }

  /**
   * Waits for an active session refresh and retries the initial route once.
   * @param {string} url
   * @returns {Observable<boolean>}
   */
  private recoverNavigation(url: string): Observable<boolean> {
    if (!this.accountService.refreshingSession$.value) {
      this.showNavigationError(url)

      return EMPTY
    }

    return this.accountService.refreshingSession$.pipe(
      filter(refreshing => !refreshing),
      take(1),
      switchMap(() => {
        this.retryingNavigation.set(true)
        return from(this.router.navigateByUrl(url))
      }),
      // Only a rejected retry is an error. A cancelled one means a guard redirected, e.g. to onboarding after logout.
      catchError(() => {
        this.showNavigationError(url)
        return EMPTY
      }),
      takeUntil(this.router.events.pipe(filter(event => event instanceof NavigationEnd))),
      finalize(() => this.retryingNavigation.set(false))
    )
  }

  /**
   * Shows a failed initial route and releases the splash screen.
   * @param {string} url
   */
  private showNavigationError(url: string): void {
    if (!this.navigationReady) {
      this.failedNavigationUrl.set(url)
      this.removeLoader()
      void this.hideSplashWhenReady()
    }
  }

  /** Hides the native splash after status-bar setup, the launch update check, and the first route are ready. */
  private async hideSplashWhenReady(): Promise<void> {
    if (
      !this.nativeReady ||
      (!this.navigationReady && !this.failedNavigationUrl()) ||
      !this.launchUpdateChecked ||
      this.splashHidden
    ) {
      return
    }

    this.splashHidden = true
    await SplashScreen.hide()
  }

  /** Retries the failed initial route without clearing the session. */
  protected async retryNavigation(): Promise<void> {
    const url = this.failedNavigationUrl()

    if (!url || this.retryingNavigation()) {
      return
    }

    this.retryingNavigation.set(true)

    try {
      await this.router.navigateByUrl(url)
    } catch {
      // The error screen stays visible until navigation succeeds.
    } finally {
      this.retryingNavigation.set(false)
    }
  }

  /**
   * Handles the back navigation, closing any open modal and minimizing the app when required
   */
  private handleBackNavigation(): void {
    const rootRoutes = ['/my-tasks', '/projects', '/timer', '/notifications', '/search']

    const listener = App.addListener('backButton', () => {
      if (this.destroyRef.destroyed) {
        return
      }

      this.ngZone.run(() => {
        // Closing sheets remain on top until their exit finishes.
        if (this.popupService.close()) {
          return
        }

        // Is in a root route, cancel back navigation and minimize the app (Android)
        if (rootRoutes.some(rootRoute => this.router.url === rootRoute)) {
          this.router.navigate([this.router.url]).then(() => {
            if (this.appService.isAndroid()) {
              App.minimizeApp().then(() => {})
            }
          })
        }

        // Normal back navigation
        else {
          this.location.back()
        }

        this.modalService.navigatingBack = false
      })
    })

    this.destroyRef.onDestroy(() => {
      void listener.then(handle => handle.remove())
    })
  }

  /**
   * Sets the app dark mode
   * @param {boolean} isDark Whether the dark theme is active.
   */
  private setDarkMode(isDark: boolean): void {
    this.isDarkMode = isDark

    if (isDark) {
      this.renderer.addClass(document.body, 'aw-2-dark')
    } else {
      this.renderer.removeClass(document.body, 'aw-2-dark')
    }
  }

  /**
   * Syncs the status bar on the next frame, once the current screen has painted.
   */
  private scheduleStatusBarSync(): void {
    requestAnimationFrame(() => void this.syncStatusBarTheme(this.appService.darkMode()))
  }

  /**
   * Lets the iOS web surface paint behind the status bar, including overlay blur and dimming.
   * Android versions with a separate status bar use the current screen or scrim color.
   * @param {boolean} isDark Whether the dark theme is active.
   */
  private async syncStatusBarTheme(isDark: boolean): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    if (Capacitor.getPlatform() === 'ios') {
      // Keep this enabled when overlays close so opening a sheet never resizes the viewport.
      await StatusBar.setOverlaysWebView({ overlay: true })
    } else {
      const overlayColor = isDark ? OVERLAY_STATUS_BAR_BACKGROUND.dark : OVERLAY_STATUS_BAR_BACKGROUND.light
      const screenColor = readScreenBackground() ?? (isDark ? STATUS_BAR_BACKGROUND.dark : STATUS_BAR_BACKGROUND.light)

      await StatusBar.setBackgroundColor({
        color: this.overlayStatusBar.hasActiveOverlay() ? overlayColor : screenColor
      })
    }

    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
  }

  /**
   * Sends the in-app theme and screen color to the iOS shell. Without this, the keyboard and the
   * gap that opens above it follow the system appearance. App builds without the handler skip this.
   * @param {MobileThemeMode} mode Theme the user chose in the app.
   */
  private syncNativeAppearance(mode: MobileThemeMode): void {
    const handler = (window as NativeAppearanceWindow).webkit?.messageHandlers?.awAppAppearance

    handler?.postMessage({
      mode,
      background: toHexColor(getComputedStyle(document.body).backgroundColor)
    })
  }

  /**
   * Initializes Sentry SDK with the app's version information
   */
  private initSentry(): void {
    from(this.mobileVersionService.initialize())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(versionInfo => {
        const contentVersion =
          versionInfo.content.kind === 'ota' ? versionInfo.content.version : versionInfo.content.kind

        sentryCapacitorInit(
          {
            dsn: 'https://8b9643e5e733475cb73167a1d60fb749@o394300.ingest.sentry.io/5556982',
            release: versionInfo.nativeVersion,
            dist: versionInfo.nativeBuild ?? undefined,
            environment,
            defaultIntegrations: false,
            integrations: [
              breadcrumbsIntegration({
                console: false,
                dom: true,
                fetch: false,
                history: true,
                sentry: false,
                xhr: false
              })
            ],
            beforeSend: (event, hint) => {
              event.extra = {
                ...event.extra,
                mobileSurfaces: { count: this.popupService.count(), legacyModalOpen: this.modalService.isModalShown() }
              }

              return this.logService.prepareSentryEvent(event, hint)
            }
          },
          options => {
            // Native reports keep the installed app release. JavaScript maps belong to this exact web build.
            init({ ...options, release: `mobile-${environment}@${sentryBuild.id}`, dist: 'web' })
            this.logService.enableSentryContext()
          }
        )

        setTag('mobile.content_version', contentVersion)
        setTag('mobile.native_build', versionInfo.nativeBuild ?? 'unknown')
        setTag('mobile.native_version', versionInfo.nativeVersion)
        this.logService.appVersion = versionInfo.nativeVersion
      })
  }

  /**
   * Removes the App's loader from the DOM
   */
  private removeLoader(): void {
    const loader = document.getElementById('loader')

    if (loader) {
      document.body.removeChild(loader)
    }
  }
}
